import { css } from '@emotion/react';

import * as React from 'react';

import { BANNER_AD_PLACEMENTS, BannerAdRowContainer } from '@coursera-web/promo-units';
import type { BannerAdPlacement } from '@coursera-web/promo-units';
import { compose } from 'recompose';

import connectToStores from 'js/lib/connectToStores';
import type UserAgentInfo from 'js/lib/useragent';

import type { Subdomain } from 'bundles/browse-common/legacy/components/Domain/types/SubDomain';
import type { DegreeListsV3 } from 'bundles/browse-common/legacy/types/degree';
import { CollectionWithSaveExperiment as Collection } from 'bundles/browse/components/Collection';
import CollectionPlaceholder from 'bundles/browse/components/CollectionPlaceholder';
import DomainPopularTopics from 'bundles/browse/components/Domain/DomainPopularTopics';
import getDomainPopularTopicsData from 'bundles/browse/components/Domain/constants/domainPopularTopicsData';
import getSubDomainPopularTopicsData from 'bundles/browse/components/Domain/constants/subDomainPopularTopicsData';
import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import { getDiscoveryCollectionLabelWithDomainOrSubdomainName } from 'bundles/browse/components/Domain/utils/DomainUtils';
import BrowseDegreeCard, { BrowseDegreeCardBare } from 'bundles/browse/components/ProductCard/BrowseDegreeCard';
import BrowseProductCard from 'bundles/browse/components/ProductCard/BrowseProductCard';
import type { DiscoveryCollection } from 'bundles/browse/components/types/DiscoveryCollections';
import {
  DEGREE_CARD_HEIGHT,
  PADDING_CHANGE_BREAKPOINT,
  PRODUCT_CARD_HEIGHT,
  PRODUCT_CARD_HEIGHT_MOBILE,
  condensedResponsiveProperty,
  getDegreeCollectionLabel,
  getMastertrackCollectionLabel,
  getUCertCollectionLabel,
} from 'bundles/browse/constants';
import { withMediaMaxWidthScreenTablet } from 'bundles/browse/utils';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

declare global {
  interface Window {
    appName?: string;
  }
}

export type DiscoveryCollectionWithMetadata = DiscoveryCollection & {
  metadata?: { header?: React.ReactNode; footer?: React.ReactNode };
};

export type DegreeListsV3WithMetadata = DegreeListsV3 & {
  metadata?: { header?: React.ReactNode; footer?: React.ReactNode };
};

export type CarouselCollection = DiscoveryCollectionWithMetadata | DegreeListsV3WithMetadata | null;

type InputProps = {
  collections: CarouselCollection[];
  isDomainPage?: boolean;
  domain?: Domain;
  subdomain?: Subdomain;
  shouldUseBareComponent?: boolean;
  isDiscoveryCollectionsLoading?: boolean;
  shouldUseCondensedView?: boolean;
};

type Props = InputProps & {
  userAgent?: UserAgentInfo;
  appName?: string;
  matches?: boolean;
};

export const condensedStyles = {
  browseCarousel: css`
    @media (max-width: ${PADDING_CHANGE_BREAKPOINT}px) {
      padding: 30px 0 !important;
    }

    .rc-BrowseCarousel {
      .slick-slide {
        min-width: 254px !important;

        & > div {
          display: flex;
          justify-content: center;
        }

        .rc-BrowseDegreeCard {
          width: 242px;
        }
      }

      .slick-prev {
        left: -55px !important;
      }

      .slick-next {
        right: -48px !important;
      }
    }

    .rc-BrowseCarousel > .slick-slider > ul.carousel-dots {
      display: block !important;

      @media (max-width: ${PADDING_CHANGE_BREAKPOINT}px) {
        margin-top: 0;
      }
    }

    .slick-list {
      margin-right: 0 !important;
    }
  `,
  placeholderCards: css`
    padding: 50px 0 !important;

    @media (max-width: ${PADDING_CHANGE_BREAKPOINT}px) {
      padding: 20px 0 !important;
    }

    .rc-BrowseProductCard {
      width: 236px !important;
      min-width: unset !important;
      height: 300px !important;

      & > div {
        height: 300px !important;
      }
    }
  `,
  loadingCarousel: css`
    .placeholder {
      .slick-track {
        display: flex;

        .slick-slide {
          width: 254px !important;
        }
      }
    }
  `,
};
export class CollectionsSection extends React.PureComponent<Props> {
  renderPromoBannerOrSocialProof = () => {
    const { domain, subdomain } = this.props;
    let placement: BannerAdPlacement = BANNER_AD_PLACEMENTS.BROWSE_HOME;
    if (subdomain) {
      placement = BANNER_AD_PLACEMENTS.BROWSE_SUBDOMAIN;
    } else if (domain) {
      placement = BANNER_AD_PLACEMENTS.BROWSE_DOMAIN;
    }

    return (
      <div key="promo-banner-or-social-proof" id="promo-banner-or-social-proof">
        <BannerAdRowContainer placement={placement} />
      </div>
    );
  };

  renderDiscoveryCollections = (collectionObject: DiscoveryCollectionWithMetadata, index: number) => {
    const { domain, subdomain, matches, shouldUseBareComponent } = this.props;

    if (!collectionObject?.entities || collectionObject.entities.length === 0) {
      return null;
    }

    const subdomainOrDomain = subdomain || domain;
    const collectionItemHeight = matches ? PRODUCT_CARD_HEIGHT_MOBILE : PRODUCT_CARD_HEIGHT;
    const splitCollectionId = collectionObject?.id.split(':');
    // DiscoveryCollection IDs have a format of `COLLECTION_TYPE:ID`, get each piece for tracking purposes.
    const collectionType = splitCollectionId?.[0];
    const collectionTypeId = splitCollectionId?.[1];

    return (
      <div key={collectionObject.id} id={collectionObject.id} className="rc-CollectionsSection">
        {/** using key and Id above to fix promo units weird rendering */}
        <Collection
          key={collectionObject.id}
          // using key here to fix UI issue rerendering the collections
          CollectionItem={BrowseProductCard}
          collectionItemHeight={collectionItemHeight}
          entities={collectionObject.entities}
          showFreeBadge={true}
          collectionName={
            subdomainOrDomain
              ? getDiscoveryCollectionLabelWithDomainOrSubdomainName(collectionObject, subdomainOrDomain)
              : collectionObject.label
          }
          collectionLink={{
            url: collectionObject.linkedCollectionPageMetadata?.url,
            label: collectionObject.label,
          }}
          header={collectionObject.metadata?.header}
          footer={collectionObject.metadata?.footer}
          trackingData={{
            label: collectionObject.label,
            id: collectionObject.id,
            collectionIndex: index,
            collectionName: collectionObject.label,
            // The following are used for MMR ranking.
            // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
            offeringSource: collectionType.toLocaleLowerCase(),
            sectionId: collectionTypeId || collectionObject?.id,
            sectionIndex: index,
            listIndex: index,
            listIdentifier: `${collectionType}:${collectionTypeId || collectionObject?.id}:${collectionObject?.label}`,
            type: collectionObject?.id,
          }}
          data-e2e="mixed-card-collection"
          shouldUseBareComponent={shouldUseBareComponent}
        />
      </div>
    );
  };

  renderDegreeCollections = (collectionItem: DegreeListsV3WithMetadata, index: number) => {
    const { shouldUseBareComponent, shouldUseCondensedView } = this.props;

    // Rendering a carousel without any items breaks its initial internal state, so don't do that.
    if (!collectionItem?.entries || collectionItem.entries.length === 0) {
      return null;
    }

    const collectionLabel = collectionItem.collectionName?.() || getDegreeCollectionLabel();

    return (
      <div key={collectionLabel} id={collectionLabel} className="rc-CollectionsSection">
        {/** using key and Id above to fix promo units weird rendering */}
        <Collection
          key={collectionLabel}
          // using key here to fix UI issue rerendering the collections
          CollectionItem={shouldUseBareComponent ? BrowseDegreeCardBare : BrowseDegreeCard}
          collectionItemHeight={DEGREE_CARD_HEIGHT}
          header={collectionItem.metadata?.header}
          footer={collectionItem.metadata?.footer}
          entities={collectionItem.entries}
          collectionName={collectionLabel}
          trackingData={{ label: collectionLabel, id: 'degrees', collectionIndex: index }}
          data-e2e="degree-card-collection"
          shouldUseBareComponent={shouldUseBareComponent}
          isDegreeOrMastertrackCollection
          styleAdditions={shouldUseCondensedView ? condensedStyles.browseCarousel : undefined}
          responsive={shouldUseCondensedView ? condensedResponsiveProperty : undefined}
          slideLength={shouldUseCondensedView ? 3 : 4}
        />
      </div>
    );
  };

  renderMasterTrackCollections = (collectionItem: DegreeListsV3WithMetadata, index: number) => {
    if (!collectionItem?.entries || collectionItem.entries.length === 0) {
      return null;
    }

    const collectionLabel = collectionItem.collectionName?.() || getMastertrackCollectionLabel();
    const { shouldUseBareComponent } = this.props;

    return (
      <div key={collectionLabel} id={collectionLabel} className="rc-CollectionsSection">
        {/** using key and Id above to fix promo units weird rendering */}
        <Collection
          key={collectionLabel}
          // using key here to fix UI issue rerendering the collections
          CollectionItem={BrowseDegreeCard}
          collectionItemHeight={DEGREE_CARD_HEIGHT}
          entities={collectionItem.entries}
          header={collectionItem.metadata?.header}
          footer={collectionItem.metadata?.footer}
          collectionName={collectionLabel}
          trackingData={{ label: collectionLabel, id: 'mastertrack', collectionIndex: index }}
          data-e2e="mastertrack-card-collection"
          shouldUseBareComponent={shouldUseBareComponent}
          isDegreeOrMastertrackCollection
        />
      </div>
    );
  };

  renderUCertsCollections = (collectionItem: DegreeListsV3WithMetadata, index: number) => {
    if (!collectionItem?.entries || collectionItem.entries.length === 0) {
      return null;
    }

    const collectionLabel = collectionItem.collectionName?.() || getUCertCollectionLabel();
    const { shouldUseBareComponent } = this.props;

    return (
      <div key={collectionLabel} id={collectionLabel} className="rc-CollectionsSection">
        {/** using key and Id above to fix promo units weird rendering */}
        <Collection
          key={collectionLabel}
          // using key here to fix UI issue rerendering the collections
          CollectionItem={BrowseDegreeCard}
          collectionItemHeight={DEGREE_CARD_HEIGHT}
          entities={collectionItem.entries}
          header={collectionItem.metadata?.header}
          footer={collectionItem.metadata?.footer}
          collectionName={collectionLabel}
          trackingData={{ label: collectionLabel, id: 'university-certificate', collectionIndex: index }}
          data-e2e="university-certificate-card-collection"
          shouldUseBareComponent={shouldUseBareComponent}
          isDegreeOrMastertrackCollection
        />
      </div>
    );
  };

  renderPopularDomainTopics = () => {
    const { domain, subdomain } = this.props;
    if (domain && !subdomain) {
      const popularDomainTopics = getDomainPopularTopicsData();
      const topicsList = popularDomainTopics[domain.id];
      return (
        topicsList && (
          <div css={{ paddingTop: '30px' }}>
            <DomainPopularTopics topicsList={topicsList} />
          </div>
        )
      );
    }
    return null;
  };

  renderPopularSubDomainTopics = () => {
    const { subdomain } = this.props;
    if (subdomain) {
      const popularSubDomainTopics = getSubDomainPopularTopicsData();
      const topicsList = popularSubDomainTopics[subdomain.id];
      return (
        topicsList && (
          <div css={{ paddingTop: '30px' }}>
            <DomainPopularTopics topicsList={topicsList} />
          </div>
        )
      );
    }
    return null;
  };

  renderCollections = () => {
    const { collections, appName } = this.props;

    if (!collections || collections.length === 0) return null;

    const collectionsComponent = collections.map((collectionItem: CarouselCollection, index: number) => {
      if (collectionItem?.__typename === 'DiscoveryCollections_productCollection') {
        return this.renderDiscoveryCollections(collectionItem as DiscoveryCollection, index);
      } else if (collectionItem?.__typename === 'DegreeListV3Mastertrack') {
        return this.renderMasterTrackCollections(collectionItem as DegreeListsV3, index);
      } else if (collectionItem?.__typename === 'DegreeListV3UniversityCertificate') {
        return this.renderUCertsCollections(collectionItem as DegreeListsV3, index);
      } else {
        return this.renderDegreeCollections(collectionItem as DegreeListsV3, index);
      }
    });

    const resolvedAppName = appName || window?.appName;
    const appNameIsBrowse = resolvedAppName?.includes('browse');

    // Adding promo units/social proof into the collections section layout, in the second spot
    const promoBannerOrSocialProof = this.renderPromoBannerOrSocialProof();
    const rowInsertPositionForSocialProof = 2;
    if (appNameIsBrowse && promoBannerOrSocialProof) {
      collectionsComponent.splice(rowInsertPositionForSocialProof, 0, promoBannerOrSocialProof);
    }

    // Adding popular domain topics into the collections section layout, in the second spot
    const popularDomainTopics = this.renderPopularDomainTopics();
    const rowInserPopularDomainTopics = 2;
    if (appNameIsBrowse && popularDomainTopics) {
      collectionsComponent.splice(rowInserPopularDomainTopics, 0, popularDomainTopics);
    }

    // Adding popular subdomain topics into the collections section layout, in the second spot
    const popularSubDomainTopics = this.renderPopularSubDomainTopics();
    const rowInserPopularSubDomainTopics = 2;
    if (appNameIsBrowse && popularSubDomainTopics) {
      collectionsComponent.splice(rowInserPopularSubDomainTopics, 0, popularSubDomainTopics);
    }

    return collectionsComponent;
  };

  render() {
    const { isDiscoveryCollectionsLoading, shouldUseCondensedView } = this.props;
    return (
      <div>
        {!isDiscoveryCollectionsLoading ? (
          this.renderCollections()
        ) : (
          <CollectionPlaceholder
            styleAdditions={
              shouldUseCondensedView ? [condensedStyles.browseCarousel, condensedStyles.placeholderCards] : undefined
            }
            numberOfPlaceholders={shouldUseCondensedView ? 3 : undefined}
          />
        )}
      </div>
    );
  }
}

export default compose<Props, InputProps>(
  connectToStores<Props, InputProps>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    userAgent: ApplicationStore.getUserAgent(),
    appName: ApplicationStore.appName,
  })),
  withMediaMaxWidthScreenTablet
)(CollectionsSection);
